import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AuctionStatusType } from '../../constants/AuctionStatusType';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';

import placeBidIcon from '../../assets/img/place-bid-icon.png';
import electricBoltIcon from '../../assets/img/electric-bolt.png';
import liveElectricBolt from '../../assets/img/live-electric-bolt.png';
import { AuctionStatus } from '../../constants/AuctionStatus';
import { formatGBP } from '../helpers/formatGBP';

const isValidPence = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0;

function getStatusForAuction(type, item) {
  if (type === AuctionStatusType.MINIMUM_PRICE) {
    return {
      text: 'No reserve',
      className: item.shouldPriceBeGreen
        ? 'auction-list-item__status auction-list-item__status__met'
        : 'auction-list-item__status auction-list-item__status__no-reserve',
    };
  }

  if (type === AuctionStatusType.PHYSICAL) {
    if (
      item &&
      (item.status === AuctionStatus.WAITING || item.status === AuctionStatus.DRAFT)
    ) {
      return {
        text: 'Reserve',
        className: 'auction-list-item__status auction-list-item__status__reserve',
      };
    }
    if (typeof item?.shouldPriceBeGreen === 'boolean') {
      return item.shouldPriceBeGreen
        ? {
            text: 'Reserve met',
            className: 'auction-list-item__status auction-list-item__status__met',
          }
        : {
            text: 'Reserve not met',
            className: 'auction-list-item__status auction-list-item__status__not-met',
          };
    }
  }

  return null;
}

const makeBidClick = (onBid, item) => (evt) => {
  if (onBid) onBid(item, evt);
};

function getSoldBuyerResultAction(item, organizationId, onBid) {
  const bidsArr = Array.isArray(item?.bids) ? item.bids : [];
  const highest = bidsArr.find((b) => b?.isHighest) || null;
  const isWon = highest && String(highest.bidderOrganizationId) === String(organizationId);
  if (isWon) {
    return {
      kind: 'won',
      label: 'WON',
      variant: CommonButtonVariants.SOLD,
      onClick: makeBidClick(onBid, item),
    };
  }
  return {
    kind: 'lost',
    label: 'LOST',
    variant: CommonButtonVariants.CANCELLED,
    onClick: makeBidClick(onBid, item),
  };
}

function getPrimaryAction({
  isOwner,
  item,
  isBuyNow,
  canBuyNow,
  onBid,
  onBuy,
  organizationId,
}) {
  if (isOwner) {
    let action = {
      kind: 'owner_live',
      label: 'LIVE',
      variant: CommonButtonVariants.LIVE,
      icon: liveElectricBolt,
      iconAlt: 'Live',
      onClick: makeBidClick(onBid, item),
    };

    if (item?.status === AuctionStatus.SOLD) {
      action = {
        kind: 'owner_sold',
        label: 'SOLD',
        variant: CommonButtonVariants.SOLD,
        onClick: makeBidClick(onBid, item),
      };
    } else if (item?.status === AuctionStatus.DRAFT) {
      action = {
        kind: 'owner_draft',
        label: 'DRAFT',
        variant: CommonButtonVariants.DRAFT,
        onClick: makeBidClick(onBid, item),
      };
    } else if (item?.status === AuctionStatus.CANCELLED) {
      action = {
        kind: 'owner_cancelled',
        label: 'CANCELLED',
        variant: CommonButtonVariants.CANCELLED,
        onClick: makeBidClick(onBid, item),
      };
    } else if (item?.status === AuctionStatus.REJECTED) {
      action = {
        kind: 'owner_rejected',
        label: 'DECLINED',
        variant: CommonButtonVariants.CANCELLED,
        onClick: makeBidClick(onBid, item),
      };
    } else if (item?.status === AuctionStatus.ENDED) {
      action = {
        kind: 'owner_ended',
        label: 'ENDED',
        variant: CommonButtonVariants.CANCELLED,
        onClick: makeBidClick(onBid, item),
      };
    } else if (item?.status === AuctionStatus.WAITING) {
      action = {
        kind: 'owner_scheduled',
        label: 'SCHEDULED',
        variant: CommonButtonVariants.LIVE,
        onClick: makeBidClick(onBid, item),
      };
    } else if (item?.status === AuctionStatus.TO_ACCEPT) {
      action = {
        kind: 'owner_to_accept',
        label: 'TO ACCEPT',
        variant: CommonButtonVariants.DRAFT,
        onClick: makeBidClick(onBid, item),
      };
    }

    return action;
  }

  if (isBuyNow) {
    let action = {
      kind: 'buy',
      label: 'BUY NOW',
      disabled: !canBuyNow,
      variant: CommonButtonVariants.OUTLINED_RED,
      icon: electricBoltIcon,
      iconAlt: 'Buy Now',
      onClick: (evt) => {
        if (!canBuyNow) return;
        if (onBuy) onBuy(item, evt);
      },
    };

    if (item?.status === AuctionStatus.DRAFT) {
      action = {
        kind: 'owner_draft',
        label: 'DRAFT',
        variant: CommonButtonVariants.DRAFT,
        onClick: makeBidClick(onBid, item),
      };
    } else if (item?.status === AuctionStatus.SOLD) {
      action = getSoldBuyerResultAction(item, organizationId, onBid);
    } else if (item?.status === AuctionStatus.TO_ACCEPT) {
      const bidsArr = Array.isArray(item?.bids) ? item.bids : [];
      const highest = bidsArr.find((b) => b?.isHighest) || null;
      const isUserOrgHighest =
        highest && String(highest.bidderOrganizationId) === String(organizationId);
      action = isUserOrgHighest
        ? {
            kind: 'owner_to_accept',
            label: 'TO ACCEPT',
            variant: CommonButtonVariants.DRAFT,
            onClick: makeBidClick(onBid, item),
          }
        : {
            kind: 'buyer_lost',
            label: 'LOST',
            variant: CommonButtonVariants.CANCELLED,
            onClick: makeBidClick(onBid, item),
          };
    } else if (item?.status === AuctionStatus.REJECTED) {
      const bidsArr = Array.isArray(item?.bids) ? item.bids : [];
      const highest = bidsArr.find((b) => b?.isHighest) || null;
      const isUserOrgHighest =
        highest && String(highest.bidderOrganizationId) === String(organizationId);
      action = isUserOrgHighest
        ? {
            kind: 'buyer_declined',
            label: 'DECLINED',
            variant: CommonButtonVariants.CANCELLED,
            onClick: makeBidClick(onBid, item),
          }
        : {
            kind: 'buyer_lost',
            label: 'LOST',
            variant: CommonButtonVariants.CANCELLED,
            onClick: makeBidClick(onBid, item),
          };
    }

    return action;
  }

  let action = {
    kind: 'bid',
    label: 'PLACE BID',
    disabled: item?.isBiddingAllowed === false,
    variant: CommonButtonVariants.RED,
    icon: placeBidIcon,
    iconAlt: 'Place Bid',
    onClick: (evt) => {
      if (onBid) onBid(item, evt);
    },
  };

  if (item?.status === AuctionStatus.SOLD) {
    action = getSoldBuyerResultAction(item, organizationId, onBid);
  } else if (item?.status === AuctionStatus.DRAFT) {
    action = {
      kind: 'owner_draft',
      label: 'DRAFT',
      variant: CommonButtonVariants.DRAFT,
      onClick: makeBidClick(onBid, item),
    };
  } else if (item?.status === AuctionStatus.CANCELLED) {
    action = {
      kind: 'owner_cancelled',
      label: 'CANCELLED',
      variant: CommonButtonVariants.CANCELLED,
      onClick: makeBidClick(onBid, item),
    };
  } else if (item?.status === AuctionStatus.REJECTED) {
    const bidsArr = Array.isArray(item?.bids) ? item.bids : [];
    const highest = bidsArr.find((b) => b?.isHighest) || null;
    const isUserOrgHighest =
      highest && String(highest.bidderOrganizationId) === String(organizationId);
    action = isUserOrgHighest
      ? {
          kind: 'buyer_declined',
          label: 'DECLINED',
          variant: CommonButtonVariants.CANCELLED,
          onClick: makeBidClick(onBid, item),
        }
      : {
          kind: 'buyer_lost',
          label: 'LOST',
          variant: CommonButtonVariants.CANCELLED,
          onClick: makeBidClick(onBid, item),
        };
  } else if (item?.status === AuctionStatus.ENDED) {
    action = {
      kind: 'owner_ended',
      label: 'ENDED',
      variant: CommonButtonVariants.CANCELLED,
      onClick: makeBidClick(onBid, item),
    };
  } else if (item?.status === AuctionStatus.TO_ACCEPT) {
    const bidsArr = Array.isArray(item?.bids) ? item.bids : [];
    const highest = bidsArr.find((b) => b?.isHighest) || null;
    const isUserOrgHighest =
      highest && String(highest.bidderOrganizationId) === String(organizationId);
    action = isUserOrgHighest
      ? {
          kind: 'owner_to_accept',
          label: 'WAITING',
          variant: CommonButtonVariants.DRAFT,
          onClick: makeBidClick(onBid, item),
        }
      : {
          kind: 'buyer_lost',
          label: 'LOST',
          variant: CommonButtonVariants.CANCELLED,
          onClick: makeBidClick(onBid, item),
        };
  }

  return action;
}

export default function useAuctionCard(item, memberId, { onBid, onBuy, isBuyNowTab } = {}) {
  const organizationId = useSelector((state) => state.auth.get('organizationId'));
  const isBuyer = useSelector((state) => state.auth.get('isBuyer'));
  const isSeller = useSelector((state) => state.auth.get('isSeller'));

  return useMemo(() => {
    const type = item?.type;
    const isBuyNow = type === AuctionStatusType.BUY_NOW_ONLY;

    const bids = Array.isArray(item?.bids) ? item.bids : [];

    let highestBidInPence = 0;
    {
      const v = item?.highestBidInPence;
      if (isValidPence(v)) highestBidInPence = v;
    }

    const flaggedHighest = bids.find((b) => b?.isHighest === true) || null;
    const isUserOrgHighest =
      flaggedHighest && String(flaggedHighest.bidderOrganizationId) === String(organizationId);

    const memberIdStr = memberId != null ? String(memberId) : '';
    const isUserBidder =
      Boolean(memberIdStr) && bids.some((b) => String(b?.bidderId) === memberIdStr);

    let isHighestBidder = false;
    if (flaggedHighest) {
      isHighestBidder = memberIdStr && String(flaggedHighest.bidderId) === memberIdStr;
    } else if (isValidPence(highestBidInPence) && highestBidInPence > 0) {
      isHighestBidder = bids.some(
        (b) =>
          String(b?.bidderId) === memberIdStr &&
          isValidPence(b?.amountInPence) &&
          b.amountInPence === highestBidInPence,
      );
    }

    const userPlacedMaxBidInPence = isUserBidder
      ? bids.reduce((max, b) => {
          if (String(b?.bidderId) !== memberIdStr) return max;
          const amt = b?.amountInPence;
          return isValidPence(amt) && amt > max ? amt : max;
        }, 0)
      : 0;

    const userProxyBidInPence = (() => {
      const v = item?.proxyBidInPence;
      return isValidPence(v) ? v : null;
    })();

    const userMaxBidInPence = isValidPence(userProxyBidInPence)
      ? Math.max(userPlacedMaxBidInPence, userProxyBidInPence)
      : userPlacedMaxBidInPence;

    let priceInPence = 0;
    if (isBuyNow) {
      if (isValidPence(item?.buyNowPriceInPence)) priceInPence = item.buyNowPriceInPence;
    } else if (isValidPence(userProxyBidInPence)) {
      priceInPence = userProxyBidInPence;
    } else {
      priceInPence = highestBidInPence;
    }
    let priceCaption = '';

    if (isBuyNowTab) {
      priceCaption = 'BUY NOW PRICE';
    } else if (isBuyNow) {
      priceCaption = 'BUY NOW PRICE';
    } else if (item?.status === AuctionStatus.PUBLISHED) {
      priceCaption = 'CURRENT BID';
    } else {
      priceCaption = 'HIGHEST BID';
    }

    let displayPriceInPence = priceInPence;
    if (!isBuyNow && displayPriceInPence === 0) {
      // if no bids yet and a minimum start price is provided, use it as the initial display price
      const hasNoBids = !isValidPence(highestBidInPence) || highestBidInPence === 0;
      const minPrice = item?.minimumPriceInPence;
      if (hasNoBids && isValidPence(minPrice)) {
        displayPriceInPence = minPrice;
      } else {
        displayPriceInPence = 15000;
      }
    }

    const status = !isBuyNow ? getStatusForAuction(type, item) : null;

    const isPublished = item?.status === AuctionStatus.PUBLISHED;
    const isToAccept =
      item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.WAITING;
    const isSold = item?.status === AuctionStatus.SOLD;
    const isRejected = item?.status === AuctionStatus.REJECTED;

    const biddingAllowed = item?.isBiddingAllowed !== false;
    const showBidBar =
      !isBuyNow && isUserBidder && (biddingAllowed || isToAccept || isSold || isRejected);

    const hasActiveUserProxy = isValidPence(userProxyBidInPence);

    let state = 'none'; // 'none' | 'outbid' | 'highest' | 'highest_proxy'
    if (showBidBar) {
      if (isUserOrgHighest) state = hasActiveUserProxy ? 'highest_proxy' : 'highest';
      else state = 'outbid';
    }

    const isOwner = (() => {
      const sellerId =
        item?.sellerId ??
        (item?.seller && typeof item.seller === 'object' ? item.seller.id : null);
      return (
        sellerId !== undefined &&
        sellerId !== null &&
        memberIdStr &&
        String(sellerId) === memberIdStr
      );
    })();

    const canBuyNow = isValidPence(item?.buyNowPriceInPence);
    let primaryAction = getPrimaryAction({
      isOwner,
      item,
      isBuyNow,
      canBuyNow,
      onBid,
      onBuy,
      organizationId,
    });

    if (!isOwner && item?.status === AuctionStatus.PUBLISHED && isSeller && !isBuyer) {
      primaryAction = {
        kind: 'restricted_seller_live',
        label: 'LIVE',
        disabled: true,
        variant: CommonButtonVariants.LIVE,
        icon: liveElectricBolt,
        iconAlt: 'Live',
        onClick: makeBidClick(onBid, item),
      };
    }

    let bidBarMessage = null;
    let bidBarVariant = null; // 'green' | 'red'
    const f = (v) => formatGBP(isValidPence(v) ? v : 0);

    if (showBidBar) {
      if (isPublished) {
        if (isUserOrgHighest) {
          if (hasActiveUserProxy) {
            bidBarMessage = `Your bid of ${f(displayPriceInPence)} is the highest. Your maximum bid is ${f(userMaxBidInPence)}.`;
          } else {
            bidBarMessage = `Your bid of ${f(highestBidInPence)} is the highest.`;
          }
          bidBarVariant = 'green';
        } else {
          bidBarMessage = `Your bid of ${f(userPlacedMaxBidInPence)} is not the highest.`;
          bidBarVariant = 'red';
        }
      } else if (isToAccept && isHighestBidder) {
        bidBarMessage = `Your bid of ${f(displayPriceInPence)} was the highest. The seller has 2 hours to accept or reject the bid`;
        bidBarVariant = 'green';
      } else if (isToAccept && isUserBidder && !isUserOrgHighest) {
        bidBarMessage = `Your bid of ${f(userPlacedMaxBidInPence)} was not the highest.`;
        bidBarVariant = 'red';
      } else if (isSold) {
        const bidsArr = Array.isArray(item?.bids) ? item.bids : [];
        const highest = bidsArr.find((b) => b?.isHighest) || null;
        const isWon =
          highest && String(highest.bidderOrganizationId) === String(organizationId);
        if (isWon) {
          const hasMax = isValidPence(userMaxBidInPence) && userMaxBidInPence > 0;
          const showMaxLine = hasMax && userMaxBidInPence > displayPriceInPence;

          bidBarMessage = showMaxLine
            ? `Your bid of ${f(displayPriceInPence)} was the highest. Your maximum bid was ${f(userMaxBidInPence)}.`
            : `Your bid of ${f(displayPriceInPence)} was the highest.`;
          bidBarVariant = 'green';
        } else if (isUserBidder) {
          bidBarMessage = `Your bid of ${f(userPlacedMaxBidInPence)} was not the highest. The winning bid was ${f(displayPriceInPence)}.`;
          bidBarVariant = 'red';
        }
      } else if (isRejected && isUserBidder) {
        if (isUserOrgHighest) {
          const declinedAmtUser = bids.reduce((max, b) => {
            if (String(b?.bidderOrganizationId) !== String(organizationId)) return max;
            if (b?.isHighest) return max;
            const amt = b?.amountInPence;
            return isValidPence(amt) && amt > max ? amt : max;
          }, 0);
          let rejectedAmt = 0;
          if (isValidPence(declinedAmtUser) && declinedAmtUser > 0) {
            rejectedAmt = declinedAmtUser;
          } else if (isValidPence(userMaxBidInPence) && userMaxBidInPence > 0) {
            rejectedAmt = userMaxBidInPence;
          } else {
            rejectedAmt = userPlacedMaxBidInPence;
          }
          bidBarMessage = `Your bid of ${f(rejectedAmt)} was declined by the seller.`;
          bidBarVariant = 'red';
        } else {
          bidBarMessage = `Your bid of ${f(userPlacedMaxBidInPence)} was not the highest.`;
          bidBarVariant = 'red';
        }
      }
    }

    return {
      priceInPence: isValidPence(priceInPence) ? priceInPence : 0,
      displayPriceInPence: isValidPence(displayPriceInPence) ? displayPriceInPence : 0,
      priceCaption: priceCaption || '',
      primaryAction: primaryAction || {
        kind: 'none',
        label: '',
        disabled: true,
        variant: CommonButtonVariants.OUTLINED,
        onClick: makeBidClick(onBid, item),
      },
      status,
      highestBidInPence: isValidPence(highestBidInPence) ? highestBidInPence : 0,
      userMaxBidInPence: isValidPence(userMaxBidInPence) ? userMaxBidInPence : 0,
      userPlacedMaxBidInPence: isValidPence(userPlacedMaxBidInPence)
        ? userPlacedMaxBidInPence
        : 0,
      isOwner: Boolean(isOwner),
      bidContext: {
        show: Boolean(showBidBar),
        state: state || 'none',
        isHighestBidder: Boolean(isHighestBidder),
        hasActiveUserProxy: Boolean(hasActiveUserProxy),
        message: bidBarMessage,
        variant: bidBarVariant,
      },
    };
  }, [item, memberId, onBid, onBuy, organizationId, isBuyer, isSeller, isBuyNowTab]);
}
